import classNames from 'classnames';
import moment from 'moment';
import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Decorator, Spinner } from '@myhealth-raccoon/components';
import { validation } from '@myhealth-raccoon/forms';

import { BookingInformationForm, WidgetFormRef, filterAppointmentTypesForDoctor } from '.';
import { AppointmentTypeOption, RadioList, RadioListRef } from '../elements';
import { AppointmentTypeEligibilityTerms } from 'src/components/AppointmentTypeEligibilityTerms.tsx';
import { CheckboxItem } from 'src/components/forms/elements/CheckboxItem';
import { VisitInfo } from 'src/services/auth';
import { AppointmentType } from 'src/services/availability';
import { availabilityService } from 'src/services/availability';
import { BookingUserProfileModel, bookingService } from 'src/services/booking';
import { UserWithSiteModel } from 'src/services/user';
import { filterToStandardAppointmentTypes, isExpressTelehealth } from 'src/utils/appointmentTypes';

type Props = {
    className?: string;
    clinics: UserWithSiteModel[];
    onNewValues?(values: BookingInformationForm): void;
    onValidate?(valid: boolean): void;
    defaultValues?: Partial<BookingInformationForm>;
    onDoctorDetails?(doctor: BookingUserProfileModel): void;
    onChangeAppointmentType?(appointmentTypeId: string): void;
    patient?: {
        displayName: string;
        dob: string;
        visitInfo: VisitInfo;
    };
    onEditDetails?(): void;
    onBookAsSomeoneElse?(): void;
};

const emptyArray: unknown[] = [];
export const BookWithDoctorForm = forwardRef<WidgetFormRef, Props>(
    (
        {
            className,
            clinics = emptyArray as UserWithSiteModel[],
            onNewValues,
            onValidate,
            defaultValues,
            onDoctorDetails,
            onChangeAppointmentType,
            patient,
            onEditDetails,
            onBookAsSomeoneElse,
        },
        ref
    ) => {
        const [doctorDetailsAtSite, setDoctorDetailsAtSite] = useState<BookingUserProfileModel>();
        const [appointmentTypesAtSite, setAppointmentTypesAtSite] = useState<AppointmentType[]>();
        const [isLoading, setIsLoading] = useState(false);
        const [_requestError, setRequestError] = useState<unknown>();

        useEffect(() => {
            if (doctorDetailsAtSite) {
                onDoctorDetails?.(doctorDetailsAtSite);
            }
        }, [onDoctorDetails, doctorDetailsAtSite]);

        const {
            handleSubmit: innerSubmit,
            register,
            control,
            formState: { errors },
            trigger,
            watch,
            setValue,
        } = useForm<BookingInformationForm>({
            defaultValues,
            shouldFocusError: false,
        });

        const patientType = watch('patientType');
        const appointmentMode = watch('appointmentMode');
        const appointmentTypeId = watch('appointmentTypeId');
        const siteId = watch('siteId');

        const site = useMemo(() => clinics.find((c) => c.siteId.toString() === siteId), [clinics, siteId]);

        const filteredAppointmentTypes = useMemo(() => {
            if (appointmentTypesAtSite && doctorDetailsAtSite) {
                return filterAppointmentTypesForDoctor(
                    appointmentTypesAtSite,
                    patientType,
                    appointmentMode,
                    doctorDetailsAtSite
                );
            }
            return [];
        }, [appointmentMode, appointmentTypesAtSite, doctorDetailsAtSite, patientType]);

        useEffect(() => {
            if (site) {
                setIsLoading(true);
                (async () => {
                    try {
                        setAppointmentTypesAtSite(undefined);
                        setDoctorDetailsAtSite(undefined);
                        const [doc, appts] = await Promise.all([
                            bookingService.GetDoctorDetails(site?.siteId, site?.erpUserId),
                            availabilityService.GetAppointmentTypes(site?.siteId),
                        ]);
                        setDoctorDetailsAtSite(doc);
                        setAppointmentTypesAtSite(filterToStandardAppointmentTypes(appts));
                        setIsLoading(false);
                    } catch (e) {
                        setRequestError(e);
                        setIsLoading(false);
                    }
                })();
            }
        }, [site]);

        const selectedAppointmentType = useMemo(
            () => filteredAppointmentTypes?.find((a) => `${a.appointmentTypeId}` === `${appointmentTypeId}`),
            [appointmentTypeId, filteredAppointmentTypes]
        );

        useEffect(() => {
            if (onNewValues) {
                innerSubmit(onNewValues)();
            }
        }, [patientType, appointmentMode, appointmentTypeId, siteId, onNewValues, innerSubmit]);

        useEffect(() => {
            onChangeAppointmentType?.(appointmentTypeId);
        }, [appointmentTypeId, onChangeAppointmentType]);

        useEffect(() => {
            if (patient) {
                setValue('patientType', patient.visitInfo.isNewPatient ? 'new' : 'existing');
            }
        }, [patient, setValue]);

        useEffect(() => {
            trigger('appointmentTypeId');
        }, [patientType, appointmentMode, siteId, appointmentTypeId, trigger]);

        useEffect(() => {
            // if the currently selected appointment type doesn't match filters
            if (!selectedAppointmentType && filteredAppointmentTypes) {
                setValue(
                    'appointmentTypeId',
                    filteredAppointmentTypes.length === 1 ? filteredAppointmentTypes[0].appointmentTypeId : null!
                );
            }
        }, [selectedAppointmentType, filteredAppointmentTypes, setValue]);

        useEffect(() => {
            if (site) {
                // whenever site changes, set doctorId with it
                setValue('doctorId', site.erpUserId.toString());
            }
        }, [setValue, site]);

        useLayoutEffect(() => {
            onValidate?.(!!site && !!selectedAppointmentType);
        }, [site, selectedAppointmentType, onValidate]);

        const appointmentTypeSelectorRef = useRef<RadioListRef>(null);
        useImperativeHandle(
            ref,
            () => ({
                openAppointmentTypeSelection: () => appointmentTypeSelectorRef.current?.openSelection(),
            }),
            []
        );

        return (
            <form className={classNames('space-y-4', className)}>
                <RadioList<UserWithSiteModel, BookingInformationForm>
                    display="all"
                    label="Clinic"
                    stringValue={site?.siteName}
                    control={control}
                    name="siteId"
                    items={clinics}
                    getItemValue={(c) => c.siteId.toString()}
                    fieldError={errors.siteId}
                    id="site"
                    value={siteId}
                    render={(c) => c.siteName}
                    rules={{ validate: validation.required('Please select a clinic') }}
                    size="lg"
                    className="px-4"
                />
                <hr className="" />
                {patient ? (
                    <div className="bg-gray-25 p-4">
                        <div className="flex flex-row flex-wrap items-start gap-x-4 gap-y-3">
                            <div className="mr-auto flex w-full flex-col items-start gap-1 sm:w-auto">
                                <span className="text-lg font-semibold text-gray-900">{patient.displayName}</span>
                                <span className="text-base font-medium text-gray-500">
                                    DOB: {moment(patient.dob).format('DD/MM/YYYY')} •{' '}
                                    {patient.visitInfo.isNewPatient ? 'New patient' : 'Existing patient'}
                                </span>
                                <div className="text-success-600 mt-2 font-semibold">
                                    Updated selections below based on your details
                                </div>
                            </div>
                            {onEditDetails && (
                                <button
                                    type="button"
                                    onClick={onEditDetails}
                                    className="text-primary font-semibold hover:underline disabled:pointer-events-none disabled:text-gray-400"
                                >
                                    Edit details
                                </button>
                            )}
                            {onBookAsSomeoneElse && (
                                <button
                                    type="button"
                                    onClick={onBookAsSomeoneElse}
                                    className="font-semibold text-gray-600 hover:underline disabled:pointer-events-none disabled:text-gray-400"
                                >
                                    Book for someone else
                                </button>
                            )}
                        </div>
                        <input
                            type="hidden"
                            {...register('patientType', { value: patient.visitInfo.isNewPatient ? 'new' : 'existing' })}
                        />
                    </div>
                ) : (
                    <Decorator
                        fieldset
                        fieldError={errors.patientType}
                        className="my-4 px-4"
                        label={
                            <span className="text-lg font-normal text-gray-600">
                                Patient Type:{' '}
                                <strong className="text-gray-700">
                                    {patientType === 'existing' && 'Existing patient'}
                                    {patientType === 'new' && 'New patient'}
                                </strong>
                            </span>
                        }
                    >
                        <div className="flex flex-row gap-3">
                            <CheckboxItem
                                {...register('patientType')}
                                type="radio"
                                id="patientType_existing"
                                value="existing"
                            >
                                Existing patient
                            </CheckboxItem>
                            <CheckboxItem {...register('patientType')} type="radio" id="patientType_new" value="new">
                                <span className="hidden sm:flex">New patient at this centre</span>
                                <span className="sm:hidden">New patient</span>
                            </CheckboxItem>
                        </div>
                    </Decorator>
                )}
                <hr />
                <Decorator
                    fieldset
                    fieldError={errors.appointmentMode}
                    className="px-4"
                    label={
                        <span className="text-lg font-normal text-gray-600">
                            Service Type:{' '}
                            <strong className="text-gray-700">
                                {appointmentMode === 'faceToFace' && 'Face to Face'}
                                {appointmentMode === 'telehealth' && 'Telehealth'}
                            </strong>
                        </span>
                    }
                >
                    <div className="flex flex-row gap-3">
                        <CheckboxItem
                            {...register('appointmentMode')}
                            type="radio"
                            id="appointmentMode_faceToFace"
                            value="faceToFace"
                        >
                            Face to Face
                            <p className="text-xs font-normal text-gray-600">
                                Consult with a healthcare provider in person at the clinic.
                            </p>
                        </CheckboxItem>
                        <CheckboxItem
                            {...register('appointmentMode')}
                            type="radio"
                            id="appointmentMode_telehealth"
                            value="telehealth"
                        >
                            Telehealth
                            <p className="text-xs font-normal text-gray-600">
                                Consult with a healthcare provider via phone or a video call.
                            </p>
                        </CheckboxItem>
                    </div>
                </Decorator>

                <hr className="md:hidden" />

                {isLoading ? (
                    <Spinner />
                ) : (
                    <>
                        <RadioList
                            className="px-4"
                            display="show-more"
                            fieldError={errors.appointmentTypeId}
                            stringValue={selectedAppointmentType?.appointmentName}
                            label="Appointment Type"
                            items={filteredAppointmentTypes}
                            rules={{ validate: validation.required('Please select an appointment type') }}
                            control={control}
                            name="appointmentTypeId"
                            value={appointmentTypeId}
                            getItemValue={(apt) => apt.appointmentTypeId}
                            render={(apt) => <AppointmentTypeOption appointmentType={apt} />}
                            id="appointment_type"
                            ref={appointmentTypeSelectorRef}
                        />
                        {selectedAppointmentType && isExpressTelehealth(selectedAppointmentType) && (
                            <AppointmentTypeEligibilityTerms
                                appointmentType={selectedAppointmentType}
                                className="my-4 px-4"
                            />
                        )}
                    </>
                )}
            </form>
        );
    }
);
